import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Badge, Button, Card, Checkbox, Dialog, ErrorBanner, FilterSelect, Input, PageHeader, Pagination, Spinner, Table, Textarea } from '../../components/ui';
import { api } from '../../lib/api';
import { dateTime, humanize } from '../../lib/format';
import { useAction, useUrlState, type Paged } from '../../lib/hooks';

interface Template { id: string; key: string; channel: 'EMAIL' | 'SMS' | 'PUSH' | 'IN_APP'; title: string; body: string; isActive: boolean; smsTemplateId: string | null }
interface Delivery { id: string; channel: string; templateKey: string; recipient: string; status: string; provider: string | null; error: string | null; attempts: number; createdAt: string; sentAt: string | null; orderId: string | null }

const CHANNELS: Template['channel'][] = ['EMAIL', 'SMS', 'PUSH', 'IN_APP'];
const CHANNEL_LABEL: Record<string, string> = { EMAIL: 'Email', SMS: 'SMS', PUSH: 'App push', IN_APP: 'In-app' };
const PLACEHOLDERS = '{{name}} {{orderNumber}} {{total}} {{courier}} {{awb}} {{trackingUrl}} {{orderUrl}} {{refundAmount}} {{storeName}} — account emails: {{name}} {{url}}';

export function Notifications() {
  const [tab, setTab] = useState<'templates' | 'log'>('templates');
  return (
    <>
      <PageHeader title="Notifications" subtitle="What customers are told, by email, SMS and app, at each step of their order." />
      <div role="tablist" className="mb-4 flex gap-2">
        {(['templates', 'log'] as const).map((t) => <Button key={t} role="tab" aria-selected={tab === t} variant={tab === t ? 'primary' : 'secondary'} onClick={() => setTab(t)}>{t === 'templates' ? 'Messages' : 'Delivery log'}</Button>)}
      </div>
      {tab === 'templates' ? <Templates /> : <Log />}
    </>
  );
}

interface Channels { email: boolean; sms: { enabled: boolean; configured: boolean }; push: boolean }

/** What the server can actually send on (these switches live in the server's .env, not here). */
function ChannelStatus() {
  const q = useQuery({ queryKey: ['channels'], queryFn: () => api<Channels>('/admin/notifications/channels'), staleTime: 60_000 });
  if (!q.data) return null;
  const c = q.data;
  const item = (label: string, on: boolean, note: string) => <li className="flex items-center gap-2"><Badge tone={on ? 'green' : 'grey'}>{on ? 'On' : 'Off'}</Badge><span><strong>{label}</strong> — {note}</span></li>;
  return (
    <Card className="mb-4">
      <ul className="space-y-1.5 text-sm">
        {item('Email', c.email, c.email ? 'sending' : 'not set up on the server yet (SMTP_URL)')}
        {item('SMS', c.sms.enabled, c.sms.enabled ? (c.sms.configured ? 'sending' : 'switched on but MSG91 key missing') : 'switched off on the server (SMS_ENABLED=false). SMS messages below will not be sent even if marked On.')}
        {item('App push', c.push, c.push ? 'sending' : 'not set up on the server yet (FCM_SERVICE_ACCOUNT)')}
      </ul>
    </Card>
  );
}

function Templates() {
  const q = useQuery({ queryKey: ['templates'], queryFn: () => api<Template[]>('/admin/notifications/templates') });
  const [editing, setEditing] = useState<Template | null>(null);
  if (q.isLoading) return <Spinner />;
  if (q.error) return <ErrorBanner error={q.error} />;
  const keys = [...new Set(q.data!.map((t) => t.key))];
  return (
    <>
      <ChannelStatus />
      <Card>
        <p className="mb-3 text-sm text-ink-muted">SMS in India must use DLT-registered text. Register each SMS message with your operator's DLT portal, then enter the approved template ID here to switch it on.</p>
        <Table rows={keys} rowKey={(k) => k} columns={[
          { header: 'Event', cell: (k) => <span className="font-medium">{humanize(k)}</span> },
          ...CHANNELS.map((ch) => ({ header: CHANNEL_LABEL[ch]!, cell: (k: string) => {
            const t = q.data!.find((x) => x.key === k && x.channel === ch);
            if (!t) return <span className="text-xs text-ink-muted">—</span>;
            return <button className="rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-600" aria-label={`Edit ${CHANNEL_LABEL[ch]} for ${humanize(k)}`} onClick={() => setEditing(t)}><Badge tone={t.isActive ? 'green' : 'grey'}>{t.isActive ? 'On' : ch === 'SMS' && !t.smsTemplateId ? 'Needs DLT ID' : 'Off'}</Badge></button>;
          } })),
        ]} />
      </Card>
      {editing && <TemplateDialog t={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function TemplateDialog({ t, onClose }: { t: Template; onClose: () => void }) {
  const [f, setF] = useState({ title: t.title, body: t.body, isActive: t.isActive, smsTemplateId: t.smsTemplateId ?? '' });
  const m = useAction(() => api(`/admin/notifications/templates/${t.key}/${t.channel}`, { method: 'PUT', body: { title: f.title, body: f.body, isActive: f.isActive, ...(t.channel === 'SMS' ? { smsTemplateId: f.smsTemplateId.trim() || null } : {}) } }), { success: 'Message saved', invalidate: [['templates']], onSuccess: onClose });
  const isSms = t.channel === 'SMS';
  return (
    <Dialog open wide title={`${humanize(t.key)} — ${CHANNEL_LABEL[t.channel]}`} onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={m.isPending} disabled={!f.title.trim() || !f.body.trim() || (isSms && f.isActive && !f.smsTemplateId.trim())} onClick={() => m.mutate(undefined)}>Save</Button></>}>
      {!isSms && <Input label={t.channel === 'EMAIL' ? 'Subject' : 'Title'} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} maxLength={200} />}
      <Textarea label={isSms ? 'Registered SMS text (for reference)' : 'Message'} rows={isSms ? 3 : 7} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} maxLength={2000}
        hint={isSms ? 'Must match the DLT-approved text. In MSG91 use ##orderNumber## style variables with the same names.' : `Placeholders: ${PLACEHOLDERS}`} />
      {isSms && <Input label="DLT template ID" value={f.smsTemplateId} onChange={(e) => setF({ ...f, smsTemplateId: e.target.value })} hint="From your DLT portal / MSG91 flow" />}
      <Checkbox label="Send this message" checked={f.isActive} onChange={(v) => setF({ ...f, isActive: v })} />
    </Dialog>
  );
}

function Log() {
  const [f, setF] = useUrlState({ status: '', channel: '', page: '1' });
  const q = useQuery({ queryKey: ['deliveries', f], queryFn: () => api<Paged<Delivery> & { last7Days: { channel: string; status: string; count: number }[] }>('/admin/notifications/deliveries', { query: { ...f, pageSize: 50 } }), placeholderData: keepPreviousData });
  if (q.isLoading) return <Spinner />;
  if (q.error) return <ErrorBanner error={q.error} />;
  const d = q.data!;
  const sum = (ch: string, st: string) => d.last7Days.filter((x) => x.channel === ch && x.status === st).reduce((a, x) => a + x.count, 0);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {(['EMAIL', 'SMS', 'PUSH'] as const).map((ch) => (
          <Card key={ch}><p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">{CHANNEL_LABEL[ch]} · last 7 days</p>
            <p className="mt-1 text-sm"><strong className="text-forest-800">{sum(ch, 'SENT')}</strong> sent · <span className={sum(ch, 'FAILED') ? 'text-danger' : ''}>{sum(ch, 'FAILED')} failed</span> · {sum(ch, 'SKIPPED')} skipped</p>
            {sum(ch, 'SKIPPED') > 0 && sum(ch, 'SENT') === 0 && <p className="mt-1 text-xs text-gold-700">Not configured on the server yet.</p>}
          </Card>
        ))}
      </div>
      <Card>
        <div className="mb-3 flex flex-wrap gap-2">
          <FilterSelect label="Status" value={f.status} onChange={(v) => setF({ status: v })} options={[{ value: '', label: 'All statuses' }, ...['SENT', 'FAILED', 'SKIPPED', 'QUEUED'].map((s) => ({ value: s, label: humanize(s) }))]} />
          <FilterSelect label="Channel" value={f.channel} onChange={(v) => setF({ channel: v })} options={[{ value: '', label: 'All channels' }, ...['EMAIL', 'SMS', 'PUSH'].map((c) => ({ value: c, label: CHANNEL_LABEL[c]! }))]} />
        </div>
        <Table rows={d.items} rowKey={(x) => x.id} empty="Nothing sent yet." columns={[
          { header: 'When', cell: (x) => <span className="whitespace-nowrap">{dateTime(x.createdAt)}</span> },
          { header: 'Message', cell: (x) => <div><div>{humanize(x.templateKey)}</div><div className="text-xs text-ink-muted">{CHANNEL_LABEL[x.channel]} → {x.recipient}</div></div> },
          { header: 'Status', cell: (x) => <div><Badge tone={x.status === 'SENT' ? 'green' : x.status === 'FAILED' ? 'red' : x.status === 'QUEUED' ? 'gold' : 'grey'}>{humanize(x.status)}</Badge>{x.error && <p className="mt-0.5 max-w-xs text-xs text-ink-muted">{x.error}</p>}</div> },
          { header: 'Tries', cell: (x) => x.attempts, className: 'text-right' },
        ]} />
        <Pagination page={d.page} totalPages={d.totalPages} total={d.total} onPage={(p) => setF({ page: String(p) } as never)} />
      </Card>
    </div>
  );
}
